// Fonte ÚNICA do cálculo de consumo médio / kWh compensável da Análise Técnica.
//
// Antes desta função a mesma conta existia copiada em 5 telas (UcAnalysisModal,
// UcDetailsReadOnly, Step4Decisao, Step2RelacaoUCs, AnaliseCadastralWizard) com
// regras DIFERENTES — a mesma UC mostrava compensável diferente em cada lugar, e
// a decisão de reprovar (GD com compensável < 1.000 kWh) saía de uma delas.
//
// Duas correções de mérito em relação ao que rodava antes:
//
// 1. Denominador único. A média de consumo dividia pelos meses preenchidos e a de
//    injeção dividia por TODAS as linhas não marcadas N/D — linha em branco
//    incluída. Com 6 meses preenchidos de 500 kWh de consumo e 500 de injeção, a
//    injeção média caía pra 250 e sobrava "compensável" de 220 kWh que não existe.
//
// 2. Injeção entra pelo BALANÇO DO PERÍODO, não como média solta. O crédito da GD
//    do cliente não vence no mês: excedente de janeiro abate o consumo de março.
//    Então soma-se o consumo do período, subtrai-se toda a injeção do período e só
//    depois se tira a média mensal. Consumo 500 e injeção 500 → compensável 0.
//
// Módulo PURO — sem React, sem rede — pra rodar no teste:
//   node --test src/lib/__tests__/analiseTecnicaConsumo.test.ts

/** Linha da grade de 12 meses (dados_tecnicos_equatorial.historyTable). */
export interface LinhaHistoricoUc {
  month?: string;
  /** Energia medida na rede, como a fatura/portal imprime (aceita '2.370' e '2.370,00'). */
  consumo?: string | number | null;
  /** Energia injetada pela GD do próprio cliente no mês (portal Equatorial). */
  injecao?: string | number | null;
  /** Mês sem registro de consumo — não entra em nenhuma média. */
  semRegistro?: boolean;
  [k: string]: unknown;
}

export interface EntradaCalculoConsumo {
  historico: LinhaHistoricoUc[] | null | undefined;
  /** 'Monofásico' | 'Bifásico' | 'Trifásico' */
  tipoLigacao?: string | null;
  /** checks.possuiGD — só `true` liga o desconto da injeção. */
  possuiGD?: boolean | null;
}

/** Um passo da explicação exibida ao analista, já com os números da UC. */
export interface PassoCalculoConsumo {
  titulo: string;
  detalhe: string;
  /** resultado do passo, formatado (ex.: '1.240 kWh/mês') */
  valor: string;
}

export interface ResultadoCalculoConsumo {
  /** Meses que entraram na conta. */
  mesesBase: number;
  /** Meses válidos jogados fora pela regra pró-cliente (0 quando há GD). */
  mesesDescartados: number;
  /** Média mensal do consumo medido, sobre `mesesBase`. */
  avgConsumo: number;
  /**
   * INDICADOR: média da injeção só nos meses em que houve injeção — mês com 0 não
   * entra, igual à regra do consumo médio. É o número que a tela mostra.
   */
  avgInjecao: number;
  /**
   * Quantos meses da base realmente tiveram injeção (denominador do `avgInjecao`).
   */
  mesesComInjecao: number;
  /**
   * CONTA: injeção diluída pelo período (Σ injeção ÷ `mesesBase`) — é esta que
   * abate o consumo, porque o crédito de um mês cobre os outros. Só coincide com
   * `avgInjecao` quando todos os meses da base injetaram.
   */
  avgInjecaoPeriodo: number;
  /** Mínimo faturado pela distribuidora — nunca é compensável. */
  custoDisponibilidade: number;
  /** O que a Vigor consegue de fato compensar por mês. */
  avgCompensavel: number;
  /** avgConsumo − avgCompensavel. Os quatro números sempre fecham entre si. */
  avgNaoCompensado: number;
  possuiGD: boolean;
  /** Total de injeção digitada que foi IGNORADA por 'possui GD' não estar em Sim. */
  injecaoIgnorada: number;
  /** true quando a base foi recortada para os últimos meses por mudança de patamar. */
  janelaCurta: boolean;
  /** Leitura da variação de patamar do histórico (ver `detectarDegrauConsumo`). */
  degrau: DegrauConsumo;
  passos: PassoCalculoConsumo[];
}

/** Custo de disponibilidade (kWh/mês) — Art. 98 da REN 1.000/2021. */
// Comparado por prefixo (o acento fica depois dele) pra não depender de
// normalização Unicode: 'TRIFÁSICO' → TRIF, 'BIFÁSICO' → BIF.
export function custoDisponibilidadeDe(tipoLigacao?: string | null): number {
  const t = String(tipoLigacao || '').toUpperCase().trim();
  if (t.startsWith('TRI')) return 100;
  if (t.startsWith('BI')) return 50;
  return 30; // Monofásico (e default de segurança)
}

/**
 * Parse do formato brasileiro: '2.370' → 2370, '1.234,56' → 1234.56.
 * Trata o glitch da Equatorial, que imprime '2.719.00' no lugar de '2.719,00'.
 */
export function parseBrNumber(value: string | number | null | undefined): number {
  const n = parseBrNumberBruto(value);
  // `parseFloat('Infinity')` devolve Infinity e passa pelo `|| 0`. Um único mês
  // corrompido assim vence qualquer corte, nunca é descartado, e propaga Infinity
  // para o consumo médio e para o compensável.
  return Number.isFinite(n) ? n : 0;
}

function parseBrNumberBruto(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  let str = String(value).trim();
  if (str.endsWith('.00')) str = str.slice(0, -3);
  if (str.includes(',')) return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
  const dotCount = (str.match(/\./g) || []).length;
  if (dotCount > 1) return parseFloat(str.replace(/\./g, '')) || 0;
  if (dotCount === 1) {
    const partes = str.split('.');
    if (partes[1].length === 3) return parseFloat(str.replace('.', '')) || 0;
  }
  return parseFloat(str) || 0;
}

/** Mediana de uma lista não vazia (com 3 valores, é simplesmente o do meio). */
function mediana(xs: number[]): number {
  const ordenados = [...xs].sort((a, b) => a - b);
  const meio = ordenados.length >> 1;
  return ordenados.length % 2 ? ordenados[meio] : (ordenados[meio - 1] + ordenados[meio]) / 2;
}

// ── Mudança de patamar (degrau) ────────────────────────────────────────────
//
// A média de 12 meses descreve mal um cliente que MUDOU de patamar no meio do
// histórico. O caso que motivou a regra: 6 meses a 600 kWh e 6 meses a 1.100.
// Média de 12 = 850, que não é o consumo de ninguém — e o rateio aloca energia
// por esse número, então falta energia para o cliente.
//
// A regra pró-cliente (`ANC-R30`) não resolve isso, porque trata mês fraco como
// ruído independente e não como patamar:
//   · degrau de SUBIDA sem GD: acerta por acidente (os meses novos são os altos);
//   · degrau de QUEDA: sobrevivem os meses ANTIGOS, altos — superdimensiona;
//   · qualquer degrau em UC com GD: pró-cliente está desligado, sai a média cheia.
//
// Por isso a janela passou a ser condicional: patamar estável → 12 meses, como
// sempre; patamar que mudou → só os meses do patamar ATUAL.

/** Acima disto a variação deixou de ser oscilação e virou outro patamar. */
export const LIMIAR_DEGRAU_PERCENT = 30;

/** Tamanho da janela quando há degrau — os meses que descrevem o patamar de hoje. */
export const MESES_JANELA_CURTA = 3;

/**
 * Abaixo disto não há "antes e depois" para comparar: 3 meses recentes contra
 * menos de 3 anteriores é ruído, não patamar. Fica na regra de 12 meses.
 */
export const MESES_MINIMOS_PARA_DEGRAU = 6;

const NOMES_MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/**
 * Rótulo do histórico → índice absoluto ano*12+mês, que é o que permite ORDENAR.
 * Aceita 'mm/aa' (o formato que o modal valida), 'mm/aaaa' e o formato por nome
 * ('mai/2025') que sai de parte das extrações. Devolve null pro que não dá pra
 * confiar — mês fora de 1..12, ano não numérico, rótulo vazio.
 */
export function mesParaIndice(rotulo?: string | null): number | null {
  if (!rotulo) return null;
  const partes = String(rotulo).trim().split('/');
  if (partes.length !== 2) return null;
  const [mesStr, anoStr] = partes;
  if (!/^\d{2,4}$/.test(anoStr)) return null;

  let mes: number;
  if (/^\d{1,2}$/.test(mesStr)) {
    mes = parseInt(mesStr, 10);
  } else {
    const nome = mesStr.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').slice(0, 3);
    mes = NOMES_MES.indexOf(nome) + 1;
  }
  if (mes < 1 || mes > 12) return null;

  let ano = parseInt(anoStr, 10);
  if (ano < 100) ano += 2000;
  return ano * 12 + (mes - 1);
}

/**
 * Ordena o histórico do mais antigo para o mais recente.
 *
 * Devolve null quando QUALQUER linha da base não tem mês legível: sem isso não
 * existe "os últimos 3 meses" — existe "as 3 últimas linhas da grade", que é
 * outra coisa e não é confiável (a grade aceita digitação em qualquer ordem, e
 * a extração de fatura já entregou histórico invertido). Nesse caso o cálculo
 * cai na regra de sempre em vez de escolher meses no chute.
 */
function ordenarPorMes(linhas: LinhaHistoricoUc[]): LinhaHistoricoUc[] | null {
  const comIndice: { linha: LinhaHistoricoUc; indice: number }[] = [];
  for (const linha of linhas) {
    const indice = mesParaIndice(linha.month);
    if (indice == null) return null;
    comIndice.push({ linha, indice });
  }
  return comIndice.sort((a, b) => a.indice - b.indice).map((x) => x.linha);
}

export interface DegrauConsumo {
  /** true quando a janela deve encolher para `MESES_JANELA_CURTA`. */
  houveDegrau: boolean;
  /** Variação do patamar atual sobre o anterior, em % (negativa = queda). */
  variacaoPercent: number | null;
  direcao: 'alta' | 'queda' | null;
  /** Média dos 3 meses mais recentes. */
  mediaRecente: number | null;
  /** Média de todos os meses anteriores a esses 3. */
  mediaAnterior: number | null;
  /** Por que a avaliação não pôde ser feita. null quando ela rodou. */
  impedimento: string | null;
}

const SEM_AVALIACAO = (impedimento: string): DegrauConsumo => ({
  houveDegrau: false,
  variacaoPercent: null,
  direcao: null,
  mediaRecente: null,
  mediaAnterior: null,
  impedimento,
});

/**
 * Compara o patamar dos 3 meses mais recentes com o dos meses anteriores.
 *
 * A comparação é de MÉDIA contra MÉDIA de propósito. Máximo contra mínimo — a
 * leitura literal de "variação no histórico" — dispara em qualquer verão e em
 * qualquer mês de leitura estimada, encurtando a janela de quase toda UC. Média
 * de 3 contra média do resto só se move quando o consumo subiu (ou caiu) e FICOU
 * lá, que é exatamente o que o vendedor não consegue enxergar de olho.
 *
 * `linhas` precisa vir em ordem cronológica crescente.
 */
export function detectarDegrauConsumo(linhas: LinhaHistoricoUc[]): DegrauConsumo {
  if (linhas.length < MESES_MINIMOS_PARA_DEGRAU) {
    return SEM_AVALIACAO(
      `Histórico com ${linhas.length} ${linhas.length === 1 ? 'mês' : 'meses'}: abaixo dos ` +
        `${MESES_MINIMOS_PARA_DEGRAU} necessários para comparar um patamar com o outro.`,
    );
  }

  const consumos = linhas.map((l) => parseBrNumber(l.consumo));
  const recentes = consumos.slice(-MESES_JANELA_CURTA);
  const anteriores = consumos.slice(0, -MESES_JANELA_CURTA);

  const media = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  const mediaRecente = media(recentes);
  const mediaAnterior = media(anteriores);

  // Base zero (UC com GD que só injetou no período anterior) não tem percentual
  // definido — dividir por ela devolveria Infinity e encurtaria a janela sempre.
  if (!(mediaAnterior > 0)) {
    return SEM_AVALIACAO('Meses anteriores sem consumo medido: não há patamar anterior para comparar.');
  }

  const variacaoPercent = ((mediaRecente - mediaAnterior) / mediaAnterior) * 100;
  const houveDegrau = Math.abs(variacaoPercent) > LIMIAR_DEGRAU_PERCENT;

  return {
    houveDegrau,
    variacaoPercent: Math.round(variacaoPercent * 10) / 10,
    direcao: variacaoPercent >= 0 ? 'alta' : 'queda',
    mediaRecente: Math.round(mediaRecente),
    mediaAnterior: Math.round(mediaAnterior),
    impedimento: null,
  };
}

const kwh = (n: number) => `${Math.round(n).toLocaleString('pt-BR')} kWh`;
const porMes = (n: number) => `${Math.round(n).toLocaleString('pt-BR')} kWh/mês`;

/**
 * Calcula consumo médio, injeção média e kWh compensável de uma UC a partir da
 * grade de 12 meses. Sempre devolve os passos da conta pra tela poder mostrar a
 * regra em vez de um número mágico.
 */
export function calcularConsumoUc({
  historico,
  tipoLigacao,
  possuiGD,
}: EntradaCalculoConsumo): ResultadoCalculoConsumo {
  const linhas = Array.isArray(historico) ? historico : [];
  const custoDisponibilidade = custoDisponibilidadeDe(tipoLigacao);
  const comGD = possuiGD === true;

  const naoDescartadas = linhas.filter((l) => !l.semRegistro);

  // Injeção digitada enquanto 'possui GD' está em Não/em branco fica de fora da
  // conta (a coluna some da grade). A tela avisa em vez de ignorar calado.
  const injecaoIgnorada = comGD
    ? 0
    : naoDescartadas.reduce((acc, l) => acc + parseBrNumber(l.injecao), 0);

  // Mês válido = linha efetivamente preenchida. Com GD, um mês pode ter consumo
  // zerado e ainda ter injeção — ele conta, senão o crédito some do balanço.
  const validos = naoDescartadas.filter((l) => {
    if (parseBrNumber(l.consumo) > 0) return true;
    return comGD && parseBrNumber(l.injecao) > 0;
  });

  if (validos.length === 0) {
    return {
      mesesBase: 0,
      mesesDescartados: 0,
      avgConsumo: 0,
      avgInjecao: 0,
      mesesComInjecao: 0,
      avgInjecaoPeriodo: 0,
      custoDisponibilidade,
      avgCompensavel: 0,
      avgNaoCompensado: 0,
      possuiGD: comGD,
      injecaoIgnorada,
      janelaCurta: false,
      degrau: SEM_AVALIACAO('Nenhum mês válido no histórico.'),
      passos: [
        {
          titulo: 'Sem meses válidos',
          detalhe:
            'Nenhum mês do histórico tem consumo preenchido. Preencha a grade de 12 meses para o cálculo rodar.',
          valor: '—',
        },
      ],
    };
  }

  // Regra PRÓ-CLIENTE: mês muito abaixo da média (casa vazia, leitura estimada)
  // puxava o consumo pra baixo, então é descartado. NÃO vale em UC com GD: lá o
  // mês baixo não é casa vazia, é a usina do cliente compensando — descartar ele
  // inventaria consumo que a Vigor não tem como compensar.
  //
  // O corte pela média tem uma patologia: a média é puxada pelo próprio outlier
  // que ela deveria filtrar. Com 1 mês de 5.000 e 11 de 1.000 a média preliminar
  // sobe pra 1.333 e SÓ o mês de 5.000 sobrevive — a base COLAPSA num único mês e
  // `avgConsumo` saía 5.000 contra 1.333 reais (3,7×). Uma digitação de 5000 no
  // lugar de 500 bastava, e o número alimenta percentual de rateio e faixa de
  // comissão.
  //
  // A regra da média foi MANTIDA para todo histórico normal — ela é a decisão
  // travada `ANC-R30` e trocá-la por "metade superior por contagem" mudaria o
  // dimensionamento de boa parte da carteira (numa varredura de 200 séries
  // sazonais plausíveis, 44,5% davam resultado diferente). O que entra aqui é só
  // uma trava de colapso: quando o corte pela média retém menos de um quarto dos
  // meses, ele deixou de ser "descartar os meses fracos" e virou "seguir um ou
  // dois valores extremos", e aí cai-se na metade superior por contagem.
  // Ordem cronológica primeiro: sem ela não existe "os últimos 3 meses". As somas
  // não dependem da ordem, então ordenar não muda nada no resto da conta.
  const ordenados = ordenarPorMes(validos);
  const degrau = ordenados
    ? detectarDegrauConsumo(ordenados)
    : SEM_AVALIACAO('Histórico sem mês legível em todas as linhas: não dá para saber quais são os meses mais recentes.');

  let base = validos;
  let mesesDescartados = 0;
  let janelaCurta = false;

  if (degrau.houveDegrau && ordenados) {
    // Patamar mudou: a janela encolhe para os meses do patamar ATUAL.
    //
    // A regra pró-cliente NÃO se aplica aqui, e isso é decisão, não esquecimento:
    // dentro de 3 meses ela deixaria 1 ou 2 na base e devolveria o pico da janela
    // em vez da média dela. Além disso o mês mais baixo dos 3 já tem explicação —
    // é o novo patamar —, que é justamente o que ela existe para descartar.
    base = ordenados.slice(-MESES_JANELA_CURTA);
    janelaCurta = true;
  } else if (!comGD) {
    const mediaPrelim =
      validos.reduce((acc, l) => acc + parseBrNumber(l.consumo), 0) / validos.length;
    let acimaDaMedia = validos.filter((l) => parseBrNumber(l.consumo) >= mediaPrelim);

    const minimoPlausivel = Math.max(2, Math.ceil(validos.length / 4));
    if (validos.length > 1 && acimaDaMedia.length < minimoPlausivel) {
      // Colapso: refaz o corte por CONTAGEM (⌈n/2⌉ maiores, empates junto), que
      // não pode ser sequestrado por um valor extremo.
      const porConsumo = [...validos].sort(
        (a, b) => parseBrNumber(b.consumo) - parseBrNumber(a.consumo),
      );
      const corte = parseBrNumber(porConsumo[Math.ceil(validos.length / 2) - 1].consumo);
      acimaDaMedia = validos.filter((l) => parseBrNumber(l.consumo) >= corte);
    }

    if (acimaDaMedia.length > 0 && acimaDaMedia.length < validos.length) {
      mesesDescartados = validos.length - acimaDaMedia.length;
      base = acimaDaMedia;
    }
  }

  const n = base.length;
  const somaConsumo = base.reduce((acc, l) => acc + parseBrNumber(l.consumo), 0);
  const somaInjecao = comGD ? base.reduce((acc, l) => acc + parseBrNumber(l.injecao), 0) : 0;

  // Dentro da janela curta o representante do mês é a MEDIANA, não a média.
  //
  // Com 12 meses um mês digitado errado se dilui; com 3 ele é um TERÇO da conta.
  // Auditoria sobre as 356 UCs de produção com análise técnica preenchida achou o
  // caso real: histórico 8.245 / 7.918 / 7.635 / 6.518 / 6.570 / 67.800 — o último
  // mês é erro de digitação de 10× e a média dos 3 devolvia 26.963 kWh para uma UC
  // de ~7.000. A mediana de 3 valores é o do meio: um mês corrompido deixa de
  // decidir o número sozinho. É a mesma proteção que a trava de colapso dá à regra
  // de 12 meses, que a janela curta não herda porque não passa pelo corte
  // pró-cliente. Em histórico sem mês corrompido as duas coincidem.
  const avgConsumo = janelaCurta
    ? Math.round(mediana(base.map((l) => parseBrNumber(l.consumo))))
    : Math.round(somaConsumo / n);

  // Duas leituras da injeção, de propósito:
  //  - avgInjecao: quanto a GD injeta NOS MESES EM QUE INJETA. Mês zerado não
  //    entra, mesma regra do consumo médio — é o indicador que a tela mostra.
  //  - avgInjecaoPeriodo: a mesma energia diluída no período, que é como ela
  //    abate o consumo (crédito de um mês cobre o mês seguinte).
  const mesesComInjecao = comGD
    ? base.filter((l) => parseBrNumber(l.injecao) > 0).length
    : 0;
  const avgInjecao = mesesComInjecao > 0 ? Math.round(somaInjecao / mesesComInjecao) : 0;
  // Na janela curta a injeção segue o consumo: usar a soma aqui reabriria pela
  // injeção a mesma porta que a mediana fechou no consumo, e os quatro números da
  // tela deixariam de fechar entre si.
  const injecaoRepresentativa = !comGD
    ? 0
    : janelaCurta
      ? Math.round(mediana(base.map((l) => parseBrNumber(l.injecao))))
      : Math.round(somaInjecao / n);
  const avgInjecaoPeriodo = injecaoRepresentativa;

  // Balanço do período: o excedente de um mês vira crédito e abate os seguintes,
  // por isso a injeção é subtraída do total antes de virar média mensal.
  const mediaLiquida = janelaCurta
    ? avgConsumo - injecaoRepresentativa
    : (somaConsumo - somaInjecao) / n;
  const avgCompensavel = Math.max(0, Math.round(mediaLiquida - custoDisponibilidade));
  const avgNaoCompensado = Math.max(0, avgConsumo - avgCompensavel);

  const passos: PassoCalculoConsumo[] = [];
  let numeroPasso = 0;
  const num = () => ++numeroPasso;

  // Passo do patamar só aparece quando a avaliação REALMENTE rodou. Histórico
  // curto ou sem mês legível não ganha um passo dizendo "não avaliei" — isso
  // seria ruído em toda UC de cliente novo.
  if (degrau.impedimento == null) {
    const sinal = (degrau.variacaoPercent ?? 0) >= 0 ? '+' : '';
    passos.push({
      titulo: `${num()}. Variação de patamar`,
      detalhe: janelaCurta
        ? `Os ${MESES_JANELA_CURTA} meses mais recentes têm média de ${porMes(degrau.mediaRecente!)}, contra ` +
          `${porMes(degrau.mediaAnterior!)} dos meses anteriores. Variação acima de ${LIMIAR_DEGRAU_PERCENT}% ` +
          `significa que o cliente mudou de patamar e se manteve nele: o consumo passa a sair só dos ` +
          `${MESES_JANELA_CURTA} meses mais recentes, senão a média descreveria um consumo que não existe mais ` +
          '(e o rateio alocaria energia por ele).'
        : `Os ${MESES_JANELA_CURTA} meses mais recentes têm média de ${porMes(degrau.mediaRecente!)}, contra ` +
          `${porMes(degrau.mediaAnterior!)} dos meses anteriores — dentro de ${LIMIAR_DEGRAU_PERCENT}%. ` +
          'O consumo está no mesmo patamar, então valem os 12 meses.',
      valor: `${sinal}${degrau.variacaoPercent!.toLocaleString('pt-BR')}%`,
    });
  }

  passos.push({
    titulo: `${num()}. Meses considerados`,
    detalhe: janelaCurta
      ? `${validos.length} ${validos.length === 1 ? 'mês preenchido' : 'meses preenchidos'} no histórico, mas ` +
        `só os ${MESES_JANELA_CURTA} mais recentes entram na conta (mudança de patamar). A regra pró-cliente ` +
        'não se aplica dentro desta janela: aqui o mês mais baixo é o novo patamar do cliente, não casa vazia.'
      : mesesDescartados > 0
        ? `${validos.length} ${validos.length === 1 ? 'mês preenchido' : 'meses preenchidos'} no histórico; ` +
          `${mesesDescartados} ${mesesDescartados === 1 ? 'ficou' : 'ficaram'} na metade de menor consumo e ` +
          `${mesesDescartados === 1 ? 'foi descartado' : 'foram descartados'} (regra pró-cliente: mês de casa vazia ou leitura estimada não pode puxar o consumo pra baixo). Meses marcados N/D e linhas em branco nunca entram.`
        : comGD
          ? `${validos.length} ${validos.length === 1 ? 'mês preenchido' : 'meses preenchidos'}. Em UC com GD a regra pró-cliente NÃO se aplica: mês de consumo baixo aqui é a usina do cliente compensando, não casa vazia. Meses marcados N/D e linhas em branco não entram.`
          : `${validos.length} ${validos.length === 1 ? 'mês preenchido' : 'meses preenchidos'}. Nenhum mês descartado. Meses marcados N/D e linhas em branco não entram.`,
    valor: `${n} ${n === 1 ? 'mês' : 'meses'}`,
  });

  passos.push({
    titulo: `${num()}. Consumo médio`,
    detalhe: janelaCurta
      ? `Valor do meio dos ${n} meses considerados (${base.map((l) => kwh(parseBrNumber(l.consumo))).join(', ')}). ` +
        'Numa janela de 3 meses usamos a mediana, e não a média, porque um mês digitado errado seria um terço ' +
        'da conta — assim ele não decide o número sozinho. Sem mês fora da curva, mediana e média dão o mesmo.'
      : `Soma do consumo medido dos meses considerados ÷ ${n}: ${kwh(somaConsumo)} ÷ ${n}.`,
    valor: porMes(avgConsumo),
  });

  if (comGD) {
    passos.push({
      titulo: `${num()}. Energia injetada pela GD do cliente`,
      detalhe:
        `${kwh(somaInjecao)} no total, em ${mesesComInjecao} ${mesesComInjecao === 1 ? 'mês' : 'meses'} ` +
        `com injeção (mês sem injeção não entra nesta média, igual à regra do consumo). ` +
        'No compensável essa energia entra diluída no período — o crédito da GD não vence no mês, ' +
        `o que sobra em um mês abate os seguintes: ${kwh(somaInjecao)} ÷ ${n} = ${porMes(avgInjecaoPeriodo)}.`,
      valor: porMes(avgInjecao),
    });
  }

  passos.push({
    titulo: `${num()}. Custo de disponibilidade`,
    detalhe: `Mínimo que a distribuidora fatura em ligação ${tipoLigacao || 'Monofásico'} (REN 1.000/2021). Nunca é compensável.`,
    valor: porMes(custoDisponibilidade),
  });

  passos.push({
    titulo: `${num()}. kWh compensável`,
    detalhe: comGD
      ? (janelaCurta
          ? `${porMes(avgConsumo)} de consumo − ${porMes(injecaoRepresentativa)} de injeção (ambos pela mediana dos ${n} meses) − ${custoDisponibilidade} de custo de disponibilidade`
          : `(${kwh(somaConsumo)} de consumo − ${kwh(somaInjecao)} de injeção) ÷ ${n} − ${custoDisponibilidade} de custo de disponibilidade`) +
        (mediaLiquida - custoDisponibilidade < 0
          ? '. Resultado negativo: a própria GD do cliente já cobre todo o consumo, não sobra nada para a Vigor compensar.'
          : '.')
      : `${porMes(avgConsumo)} − ${custoDisponibilidade} kWh de custo de disponibilidade.`,
    valor: porMes(avgCompensavel),
  });

  return {
    mesesBase: n,
    mesesDescartados,
    avgConsumo,
    avgInjecao,
    mesesComInjecao,
    avgInjecaoPeriodo,
    custoDisponibilidade,
    avgCompensavel,
    avgNaoCompensado,
    possuiGD: comGD,
    injecaoIgnorada,
    janelaCurta,
    degrau,
    passos,
  };
}

/** Atalho para quem só precisa do consumo médio (listas, divergência do vendedor). */
export function consumoMedioUc(entrada: EntradaCalculoConsumo): number {
  return calcularConsumoUc(entrada).avgConsumo;
}
